import { useEffect, useMemo, useState } from 'react';

import { useApiClient } from '@eluve/api-client-provider';
import { cacheUtils, useCompleteFragment } from '@eluve/apollo-client';
import { LanguageSelectorComboBox } from '@eluve/blocks';
import {
  Alert,
  AlertDescription,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Icon,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Stack,
  Text,
} from '@eluve/components';
import {
  TRANSCRIPT_TAB,
  appointmentLanguagesFragment,
  appointmentTemplateBundleFragment,
  useAppointmentActivity,
  useAppointmentTabNav,
  useTenantBundles,
} from '@eluve/frontend-appointment-hooks';
import { DEFAULT_LANGUAGE } from '@eluve/language-utils';
import { useIsFeatureFlagEnabled } from '@eluve/smart-blocks';

interface RegenerateSummaryProps {
  disabled?: boolean;
  tenantId: string;
  appointmentId: string;
}

const getAdminRegenerationErrorMessage = (body: unknown) => {
  if (
    typeof body === 'object' &&
    body !== null &&
    'message' in body &&
    typeof body.message === 'string'
  ) {
    return body.message;
  }

  return 'Failed to start summary regeneration';
};

export const RegenerateSummary: React.FC<RegenerateSummaryProps> = ({
  disabled,
  tenantId,
  appointmentId,
}) => {
  const apiClient = useApiClient();
  const { setTab } = useAppointmentTabNav();
  const { isSummarizationInProgress } = useAppointmentActivity();
  // Gate on the TENANT flag, not the tenant-or-user flag: multilingual is a
  // tenant capability and the admin regenerate endpoint validates it at tenant
  // scope. Using the tenant-or-user flag here let an eluve-admin who has the
  // user-level flag send a language for a non-multilingual tenant, which the
  // backend then rejected — blocking regeneration entirely (ENG-5002).
  const multilingualSupportEnabled = useIsFeatureFlagEnabled(
    'MULTILINGUAL_SUPPORT',
  );
  const { bundles, loading: bundlesLoading } = useTenantBundles(tenantId);
  const appointmentTemplateBundle = useCompleteFragment({
    fragment: appointmentTemplateBundleFragment,
    key: { id: appointmentId },
    strict: false,
  });
  const appointmentLanguages = useCompleteFragment({
    fragment: appointmentLanguagesFragment,
    key: { id: appointmentId },
    strict: false,
  });

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string>();
  const [selectedTemplateBundleId, setSelectedTemplateBundleId] = useState('');
  const [selectedInputLanguage, setSelectedInputLanguage] =
    useState(DEFAULT_LANGUAGE);
  const [selectedOutputLanguage, setSelectedOutputLanguage] =
    useState(DEFAULT_LANGUAGE);
  const [reTranscribe, setReTranscribe] = useState(false);

  const selectedTemplateBundle = useMemo(
    () => bundles.find((bundle) => bundle.id === selectedTemplateBundleId),
    [bundles, selectedTemplateBundleId],
  );
  const selectedTemplateBundleName =
    selectedTemplateBundle && selectedTemplateBundle.displayName.length > 0
      ? selectedTemplateBundle.displayName
      : 'Untitled bundle';

  useEffect(() => {
    if (isDialogOpen) {
      setError(undefined);
      setReTranscribe(false);
    }
  }, [isDialogOpen]);

  useEffect(() => {
    if (!isDialogOpen) return;

    setSelectedTemplateBundleId((current) => {
      const storedBundleId = appointmentTemplateBundle?.initialTemplateBundleId;
      const currentIsValid = bundles.some((bundle) => bundle.id === current);
      const storedIsValid = bundles.some(
        (bundle) => bundle.id === storedBundleId,
      );

      if (currentIsValid) return current;
      if (storedBundleId && storedIsValid) return storedBundleId;
      return bundles[0]?.id ?? '';
    });
  }, [
    appointmentTemplateBundle?.initialTemplateBundleId,
    bundles,
    isDialogOpen,
  ]);

  useEffect(() => {
    if (!isDialogOpen) return;

    setSelectedInputLanguage(
      appointmentLanguages?.inputLanguage ?? DEFAULT_LANGUAGE,
    );
    setSelectedOutputLanguage(
      appointmentLanguages?.outputLanguage ?? DEFAULT_LANGUAGE,
    );
  }, [
    appointmentLanguages?.inputLanguage,
    appointmentLanguages?.outputLanguage,
    isDialogOpen,
  ]);

  const handleSubmit = async () => {
    setError(undefined);
    setProcessing(true);
    try {
      const reTranscribeRequested = multilingualSupportEnabled && reTranscribe;
      const languagePayload = multilingualSupportEnabled
        ? {
            outputLanguage: selectedOutputLanguage,
            ...(reTranscribeRequested && {
              inputLanguage: selectedInputLanguage,
            }),
          }
        : {};

      const result = await apiClient.adminAppointment.regenerateSummary({
        params: {
          tenantId,
          appointmentId,
        },
        body: reTranscribeRequested
          ? {
              templateBundleId: selectedTemplateBundleId,
              reTranscribe: true,
              inputLanguage: selectedInputLanguage,
              outputLanguage: selectedOutputLanguage,
            }
          : {
              templateBundleId: selectedTemplateBundleId,
              reTranscribe: false,
              ...(multilingualSupportEnabled && {
                outputLanguage: selectedOutputLanguage,
              }),
            },
      });

      if (result.status !== 201) {
        throw new Error(getAdminRegenerationErrorMessage(result.body));
      }

      // The regenerate call is a ts-rest POST, so it doesn't update the Apollo
      // cache the dialog reads from. Write the persisted selections back so
      // reopening (even after a remount) seeds from them, not stale values.
      cacheUtils.updateFragment(
        {
          fragment: appointmentTemplateBundleFragment,
          key: { id: appointmentId },
        },
        (existing) =>
          existing
            ? { ...existing, initialTemplateBundleId: selectedTemplateBundleId }
            : existing,
      );
      if (multilingualSupportEnabled) {
        cacheUtils.updateFragment(
          {
            fragment: appointmentLanguagesFragment,
            key: { id: appointmentId },
          },
          (existing) =>
            existing ? { ...existing, ...languagePayload } : existing,
        );
      }

      setTab(TRANSCRIPT_TAB);
      setIsDialogOpen(false);
    } catch (error) {
      setError(error instanceof Error ? error.message : String(error));
    } finally {
      setProcessing(false);
    }
  };

  return (
    <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen} modal>
      <DialogTrigger asChild>
        <Button variant="outline" disabled={disabled}>
          <Icon name="RotateCw" size="auto" />
          Regenerate Summary
        </Button>
      </DialogTrigger>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>Regenerate Summary</DialogTitle>
        </DialogHeader>
        <Stack gap={3}>
          {isSummarizationInProgress && (
            <Alert variant="warning">
              <Icon name="AlertTriangle" />
              <AlertDescription>
                A summarization is already in progress. Saving will start a new
                one.
              </AlertDescription>
            </Alert>
          )}
          {error && (
            <Text size="md" className="text-text-negative">
              {error}
            </Text>
          )}
          <Stack gap={2}>
            <Label>Template bundle</Label>
            <Select
              value={selectedTemplateBundleId}
              onValueChange={setSelectedTemplateBundleId}
              disabled={processing || bundlesLoading || bundles.length === 0}
            >
              <SelectTrigger aria-label="Template bundle">
                <SelectValue placeholder="Select a template bundle" />
              </SelectTrigger>
              <SelectContent usesPortal={false}>
                {bundles.map((bundle) => (
                  <SelectItem key={bundle.id} value={bundle.id}>
                    {bundle.displayName.length > 0
                      ? bundle.displayName
                      : 'Untitled bundle'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {bundles.length === 0 && !bundlesLoading && (
              <Text size="sm" color="supporting">
                No enabled template bundles are available for this tenant.
              </Text>
            )}
          </Stack>
          {multilingualSupportEnabled && (
            <>
              <Stack gap={2}>
                <Label>Note language</Label>
                <LanguageSelectorComboBox
                  ariaLabelPrefix="Note language"
                  selectedLanguageCode={selectedOutputLanguage}
                  onSelectedLanguageCode={async (languageCode) => {
                    setSelectedOutputLanguage(languageCode);
                  }}
                  disabled={processing}
                  showLanguageIcon={false}
                  selectButtonStyles="w-full"
                />
              </Stack>
              <Stack
                gap={3}
                className="rounded-md border border-border-tertiary bg-bg-secondary p-3"
              >
                <Stack gap={2} direction="horizontal" align="start">
                  <Checkbox
                    checked={reTranscribe}
                    onCheckedChange={(checked) =>
                      setReTranscribe(checked === true)
                    }
                    disabled={processing}
                    aria-label="Re-transcribe with new input language"
                    id="admin-regen-retranscribe"
                  />
                  <Stack gap={0.5}>
                    <Label htmlFor="admin-regen-retranscribe">
                      Re-transcribe audio
                    </Label>
                    <Text size="sm" color="supporting">
                      Re-run transcription with a corrected spoken language.
                    </Text>
                  </Stack>
                </Stack>
                {reTranscribe && (
                  <Stack gap={2}>
                    <Label>Spoken language</Label>
                    <LanguageSelectorComboBox
                      ariaLabelPrefix="Spoken language"
                      selectedLanguageCode={selectedInputLanguage}
                      onSelectedLanguageCode={async (languageCode) => {
                        setSelectedInputLanguage(languageCode);
                      }}
                      disabled={processing}
                      showLanguageIcon={false}
                      selectButtonStyles="w-full"
                    />
                  </Stack>
                )}
              </Stack>
            </>
          )}
          {selectedTemplateBundle && (
            <Text size="sm" color="supporting">
              This will persist {selectedTemplateBundleName} on the appointment
              before regenerating.
            </Text>
          )}
        </Stack>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => setIsDialogOpen(false)}
            disabled={processing}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={
              disabled === true ||
              processing ||
              bundlesLoading ||
              selectedTemplateBundleId.length === 0
            }
            loading={processing}
          >
            Save and Regenerate
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
